/**
 * `apps/web` i18n bootstrap — English + Spanish locales for the static
 * marketing pages (GitHub issue — "Create React components for static
 * marketing pages using vite-react-ssg", Requirement #5: "Integrate i18n
 * support for English and Spanish locales, loading locale JSON files from
 * `src/lib/i18n/`").
 *
 * `en.json` / `es.json` live alongside this module and are imported
 * directly (`resolveJsonModule` is already enabled in `tsconfig.json`) so
 * resources are available synchronously — required for `vite-react-ssg`,
 * which imports this app's entry point (`src/main.tsx`) in Node at build
 * time and server-renders every marketing route in the same pass. An
 * async locale backend (e.g. `i18next-http-backend`) would render the
 * very first paint (and therefore the pre-rendered static HTML) with raw
 * translation keys instead of real copy, which is unacceptable for a
 * page whose entire value proposition is fully-rendered, crawlable HTML.
 *
 * `initImmediate: false` forces `i18next.init()` to resolve synchronously
 * (no microtask/backend round-trip) for the same reason.
 *
 * `en` is both the initial `lng` and the `fallbackLng`: every marketing
 * page is statically generated once, in English, at build time (see
 * `../../app/marketingRoutes.ts`); `es` is loaded as an additional
 * resource bundle so `i18n.changeLanguage('es')` works for client-side
 * language switching after hydration and is exercised directly in unit
 * tests, without requiring a second, localized SSG build per locale.
 *
 * Mirrors the root `src/lib/i18n.ts` bootstrap (added for issue #170)
 * with the same `isInitialized` guard so importing this module from
 * multiple marketing pages (or from a test) is a safe no-op after the
 * first call, rather than re-initializing/clobbering i18next's state.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './en.json';
import es from './es.json';

export const SUPPORTED_LOCALES = ['en', 'es'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';

if (!i18n.isInitialized) {
  // `initImmediate: false` makes `.init()` resolve synchronously, so the
  // returned promise is already settled by the time this call returns.
  void i18n.use(initReactI18next).init({
    lng: DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    interpolation: { escapeValue: false },
    initImmediate: false,
  });
}

export default i18n;
